import React, { useState } from 'react';
import { Tab, Tabs, Accordion, Alert, Badge, Table } from 'react-bootstrap';
import ProveedoresTab from '../../components/ContabilidadConfig/ProveedoresTab';
import EndpointsTab from '../../components/ContabilidadConfig/EndpointsTab';
import FieldMappingsTab from '../../components/ContabilidadConfig/FieldMappingsTab';

const TABS = [
    { key: 'providers',      label: 'Proveedores' },
    { key: 'endpoints',      label: 'Endpoints' },
    { key: 'field-mappings', label: 'Mapeo de Campos' }
];

// ── Bloques reutilizables del manual ──────────────────────────────────────────

const FONT_CODE = { fontFamily: 'monospace', fontSize: 12 };
const PRE_STYLE = { fontSize: 12, padding: 10, borderRadius: 6 };

function Codigo({ children }) {
    return <code style={FONT_CODE}>{children}</code>;
}

function Ejemplo({ titulo, children, variant = 'light' }) {
    return (
        <div className={`mb-2 border rounded p-2 bg-${variant}`}>
            <div className="fw-semibold small mb-1 text-muted">{titulo}</div>
            <pre className="bg-white mb-0" style={PRE_STYLE}>{children}</pre>
        </div>
    );
}

// ── Contenido de cada tab del manual ───────────────────────────────────────────

function TabInicio() {
    return (
        <div>
            <Alert variant="info" className="small">
                Esta pantalla configura la integracion entre Tinvaz y un software contable externo
                (Alegra, Siigo, QuickBooks, etc.) sin escribir codigo. Toda la configuracion vive
                en la base de datos y se puede ajustar en caliente.
            </Alert>

            <h6 className="fw-bold">Las 3 piezas de la configuracion</h6>
            <Table size="sm" bordered className="small">
                <thead className="table-light">
                    <tr><th style={{ width: '18%' }}>Pieza</th><th>¿Que define?</th><th>¿Donde se configura?</th></tr>
                </thead>
                <tbody>
                    <tr>
                        <td><strong>Proveedor</strong></td>
                        <td>El software contable: URL base + autenticacion + headers globales.</td>
                        <td>Tab <Badge bg="primary">Proveedores</Badge></td>
                    </tr>
                    <tr>
                        <td><strong>Endpoint</strong></td>
                        <td>Ruta concreta dentro del proveedor para un tipo de documento (cliente, comprobante, cuenta contable…). Incluye la forma de la respuesta cuando es lectura.</td>
                        <td>Tab <Badge bg="primary">Endpoints</Badge></td>
                    </tr>
                    <tr>
                        <td><strong>Mapeo de campos</strong></td>
                        <td>Como se traducen los campos entre el modelo Tinvaz y el JSON del proveedor.</td>
                        <td>Tab <Badge bg="primary">Mapeo de Campos</Badge></td>
                    </tr>
                </tbody>
            </Table>

            <h6 className="fw-bold mt-3">Conceptos clave</h6>
            <Accordion flush>
                <Accordion.Item eventKey="0">
                    <Accordion.Header><strong>Direccion del mapeo</strong> — outbound vs inbound</Accordion.Header>
                    <Accordion.Body className="small">
                        <p className="mb-2">Cada mapeo tiene una direccion porque la transformacion no es simetrica:</p>
                        <ul className="mb-2">
                            <li><Badge bg="primary">outbound</Badge> Tinvaz → proveedor. Se usa al <strong>enviar</strong> documentos (POST de comprobantes, facturas).</li>
                            <li><Badge bg="info">inbound</Badge> proveedor → Tinvaz. Se usa al <strong>leer</strong> datos (GET de cuentas contables, terceros).</li>
                        </ul>
                        <p className="mb-0 text-muted">
                            El mismo par <Codigo>internal_path ↔ external_path</Codigo> puede tener un mapeo
                            outbound y otro inbound con configuraciones distintas (transforms, defaults, lookups inversos).
                        </p>
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="1">
                    <Accordion.Header><strong>Endpoints de lectura</strong> vs <strong>endpoints de envio</strong></Accordion.Header>
                    <Accordion.Body className="small">
                        <ul className="mb-0">
                            <li><strong>Envio (POST):</strong> Tinvaz manda un documento al proveedor. Solo necesita endpoint + headers + mapeos outbound.</li>
                            <li><strong>Lectura (GET):</strong> Tinvaz consulta data del proveedor (ej: el PUC de Alegra). Necesita ademas <Codigo>response_shape</Codigo> para entender la forma de la respuesta + mapeos inbound.</li>
                        </ul>
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="2">
                    <Accordion.Header><strong>Flujo recomendado</strong> al configurar un proveedor nuevo</Accordion.Header>
                    <Accordion.Body className="small">
                        <ol className="mb-0">
                            <li>Crear el <strong>Proveedor</strong> con URL base y credenciales.</li>
                            <li>Crear los <strong>Endpoints</strong> uno por tipo de documento que vas a integrar.</li>
                            <li>En cada endpoint de lectura, configurar el <Codigo>response_shape</Codigo>.</li>
                            <li>Crear los <strong>Mapeos</strong> arrastrando del arbol de campos. Empezar por outbound (POST) o inbound (GET) segun el caso.</li>
                            <li>Probar con el <strong>Panel de Prueba</strong> (outbound) o ejecutando el endpoint real (inbound).</li>
                        </ol>
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="3">
                    <Accordion.Header><strong>Glosario rapido</strong></Accordion.Header>
                    <Accordion.Body className="small">
                        <dl className="row mb-0">
                            <dt className="col-sm-3"><Codigo>internal_path</Codigo></dt>
                            <dd className="col-sm-9">Ruta del campo en el modelo Tinvaz. Ej: <Codigo>cliente.nit</Codigo>.</dd>
                            <dt className="col-sm-3"><Codigo>external_path</Codigo></dt>
                            <dd className="col-sm-9">Ruta del campo en el JSON del proveedor. Ej: <Codigo>identification</Codigo>.</dd>
                            <dt className="col-sm-3"><Codigo>value_source</Codigo></dt>
                            <dd className="col-sm-9">De donde se toma el valor: <Codigo>internal</Codigo>, <Codigo>default</Codigo>, <Codigo>fixed</Codigo>, <Codigo>calculated</Codigo>.</dd>
                            <dt className="col-sm-3"><Codigo>transform</Codigo></dt>
                            <dd className="col-sm-9">Transformacion aplicada al valor (number, date_iso, uppercase, lookup_table, etc.).</dd>
                            <dt className="col-sm-3"><Codigo>lookup_map</Codigo></dt>
                            <dd className="col-sm-9 mb-0">Diccionario JSON para <Codigo>transform=lookup_table</Codigo>. Mapea claves a valores (ej: <Codigo>{`{"debit":"Débito"}`}</Codigo>).</dd>
                        </dl>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </div>
    );
}

function TabProveedores() {
    return (
        <div>
            <Alert variant="light" className="small border">
                Un <strong>Proveedor</strong> es el software contable externo. Define como conectarse a el:
                URL base, autenticacion y headers globales. Despues vas a crear endpoints y mapeos contra este proveedor.
            </Alert>

            <Accordion defaultActiveKey="0" alwaysOpen>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>1️⃣ Crear un proveedor — paso a paso</Accordion.Header>
                    <Accordion.Body className="small">
                        <ol>
                            <li>Haz clic en <strong>+ Agregar Proveedor</strong>.</li>
                            <li>Completa los campos basicos:
                                <ul>
                                    <li><strong>Nombre interno</strong>: identificador unico en minusculas, sin espacios (<Codigo>alegra</Codigo>, <Codigo>siigo_nube</Codigo>). El backend lo usa para identificar al proveedor en el codigo.</li>
                                    <li><strong>Nombre para mostrar</strong>: texto visible (<em>Alegra</em>, <em>Siigo Nube</em>).</li>
                                    <li><strong>URL base</strong>: raiz de la API (sin la ruta del endpoint).</li>
                                </ul>
                            </li>
                            <li>Configura la <strong>autenticacion</strong> (ver siguiente seccion).</li>
                            <li>Opcionalmente agrega <strong>Headers por defecto</strong> en JSON. Se enviaran en todas las peticiones a este proveedor y los endpoints los pre-cargan al crearse.</li>
                            <li>Guarda.</li>
                        </ol>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                    <Accordion.Header>2️⃣ Tipos de autenticacion</Accordion.Header>
                    <Accordion.Body className="small">
                        <Table size="sm" bordered>
                            <thead className="table-light">
                                <tr><th>Tipo</th><th>Cuando usar</th><th>Campos requeridos</th></tr>
                            </thead>
                            <tbody>
                                <tr><td><Badge bg="secondary">none</Badge></td><td>API publica sin auth (raro).</td><td>—</td></tr>
                                <tr><td><Badge bg="secondary">basic</Badge></td><td>Alegra y la mayoria de SaaS contables clasicos.</td><td><Codigo>username</Codigo>, <Codigo>password</Codigo></td></tr>
                                <tr><td><Badge bg="secondary">bearer</Badge></td><td>Siigo, QuickBooks: API con tokens JWT/OAuth.</td><td><Codigo>token</Codigo></td></tr>
                                <tr><td><Badge bg="secondary">apiKey</Badge></td><td>APIs que envian la clave en un header custom.</td><td><Codigo>header</Codigo>, <Codigo>value</Codigo></td></tr>
                            </tbody>
                        </Table>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                    <Accordion.Header>3️⃣ Ejemplos de configuracion</Accordion.Header>
                    <Accordion.Body className="small">
                        <Ejemplo titulo="Alegra (Basic Auth)">
{`Nombre interno:   alegra
Nombre visible:   Alegra
URL base:         https://api.alegra.com
Auth:             basic
  username:       mi@correo.com
  password:       mi_token_alegra
Headers:          { "Content-Type": "application/json" }`}
                        </Ejemplo>
                        <Ejemplo titulo="Siigo Nube (Bearer Token)">
{`Nombre interno:   siigo_nube
Nombre visible:   Siigo Nube
URL base:         https://api.siigo.com
Auth:             bearer
  token:          eyJhbGciOiJSUzI1NiJ9...
Headers:          { "Partner-Id": "biolab" }`}
                        </Ejemplo>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="3">
                    <Accordion.Header>4️⃣ Desactivar o eliminar</Accordion.Header>
                    <Accordion.Body className="small">
                        <p className="mb-1">Usa <strong>Desactivar</strong> para apagar la integracion sin perder la configuracion.</p>
                        <p className="mb-1">No se puede desactivar un proveedor con mapeos activos: primero desactiva los mapeos o reasignalos.</p>
                        <p className="mb-0">Para reactivar, edita y marca <strong>Estado: Activo</strong>.</p>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </div>
    );
}

function TabEndpoints() {
    return (
        <div>
            <Alert variant="light" className="small border">
                Un <strong>Endpoint</strong> conecta un tipo de documento Tinvaz con una ruta del proveedor.
                Cada proveedor tiene <em>un endpoint por tipo de documento</em>. La URL final es{' '}
                <Codigo>provider.base_url + endpoint</Codigo>.
            </Alert>

            <Accordion defaultActiveKey="0" alwaysOpen>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>1️⃣ Crear un endpoint — paso a paso</Accordion.Header>
                    <Accordion.Body className="small">
                        <ol>
                            <li>Haz clic en <strong>+ Agregar Endpoint</strong>.</li>
                            <li>Selecciona el <strong>Proveedor</strong>. Los headers se pre-cargan automaticamente desde los headers globales del proveedor.</li>
                            <li>Selecciona el <strong>Tipo de documento</strong>.</li>
                            <li>Escribe el <strong>Endpoint</strong>: ruta relativa, debe empezar con <Codigo>/</Codigo>.</li>
                            <li>Ajusta los <strong>Headers</strong> si este endpoint necesita algo distinto al resto.</li>
                            <li>Si este endpoint <strong>lee datos</strong> del proveedor, abre la seccion <strong>Forma de la respuesta</strong> y configurala. Si solo envia, dejala vacia.</li>
                        </ol>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                    <Accordion.Header>2️⃣ Tipos de documento — endpoints tipicos</Accordion.Header>
                    <Accordion.Body className="small">
                        <Table size="sm" bordered>
                            <thead className="table-light">
                                <tr><th>Tipo de documento</th><th>Direccion tipica</th><th>Alegra</th><th>Siigo</th></tr>
                            </thead>
                            <tbody>
                                <tr><td>Cotizacion / Propuesta</td><td>outbound</td><td><Codigo>/api/v1/estimates</Codigo></td><td><Codigo>/v1/quotes</Codigo></td></tr>
                                <tr><td>Cliente</td><td>outbound + inbound</td><td><Codigo>/api/v1/contacts</Codigo></td><td><Codigo>/v1/contacts</Codigo></td></tr>
                                <tr><td>Producto / Item</td><td>outbound + inbound</td><td><Codigo>/api/v1/items</Codigo></td><td><Codigo>/v1/products</Codigo></td></tr>
                                <tr><td>Comprobante contable</td><td>outbound</td><td><Codigo>/api/v1/journal-entries</Codigo></td><td><Codigo>/v1/accounting-entries</Codigo></td></tr>
                                <tr><td>Cuenta contable (PUC)</td><td><strong>inbound</strong></td><td><Codigo>/api/v1/categories</Codigo></td><td><Codigo>/v1/account-groups</Codigo></td></tr>
                            </tbody>
                        </Table>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                    <Accordion.Header>3️⃣ Headers — herencia desde el proveedor</Accordion.Header>
                    <Accordion.Body className="small">
                        <p>Al seleccionar el proveedor, los headers globales del proveedor se copian a este endpoint. Puedes editarlos para sobrescribir o agregar headers especificos.</p>
                        <Ejemplo titulo="Headers heredados del proveedor + override por endpoint">
{`// Headers globales del proveedor:
{
  "Content-Type": "application/json",
  "Accept": "application/json"
}

// Override en este endpoint (ej: forzar version de la API):
{
  "Content-Type": "application/json",
  "Accept": "application/json",
  "X-API-Version": "2024-01"
}`}
                        </Ejemplo>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="3">
                    <Accordion.Header>
                        4️⃣ Forma de la respuesta (<Codigo>response_shape</Codigo>) — <Badge bg="info">solo lectura</Badge>
                    </Accordion.Header>
                    <Accordion.Body className="small">
                        <p>
                            Solo aplica a endpoints que <strong>leen datos</strong> del proveedor. El sistema usa estos
                            campos para normalizar respuestas heterogeneas (arbol vs array plano, paginadas, etc) a
                            un array uniforme de items antes de mapear.
                        </p>

                        <Table size="sm" bordered>
                            <thead className="table-light">
                                <tr><th>Campo</th><th>Descripcion</th></tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><Codigo>root_path</Codigo></td>
                                    <td>Ruta donde esta el array dentro de la respuesta. Vacio si el array es la raiz.<br/>
                                        Alegra: vacio. Siigo: <Codigo>results</Codigo>. QuickBooks: <Codigo>QueryResponse.Account</Codigo>.</td>
                                </tr>
                                <tr>
                                    <td><Codigo>is_tree</Codigo></td>
                                    <td>Marcar si la respuesta es un arbol recursivo (cada nodo tiene hijos en otro campo).<br/>
                                        Alegra cuentas: ✅. Siigo terceros: ❌.</td>
                                </tr>
                                <tr>
                                    <td><Codigo>children_field</Codigo></td>
                                    <td>Nombre del campo con los hijos. Solo si <Codigo>is_tree</Codigo>.<br/>
                                        Alegra: <Codigo>children</Codigo>.</td>
                                </tr>
                                <tr>
                                    <td><Codigo>leaf_filter.field</Codigo> + <Codigo>.equals</Codigo></td>
                                    <td>Filtrar solo los nodos cuyo campo tenga ese valor exacto. Util para descartar cuentas acumulativas y traer solo cuentas operativas.<br/>
                                        Alegra: <Codigo>use</Codigo> = <Codigo>movement</Codigo>.</td>
                                </tr>
                                <tr>
                                    <td><Codigo>pagination</Codigo></td>
                                    <td>Para proveedores que paginan. Por ahora se documenta en la config; el motor lee la primera pagina.<br/>
                                        Siigo: <Codigo>style:'url'</Codigo>, <Codigo>next_path:'next'</Codigo>.</td>
                                </tr>
                            </tbody>
                        </Table>

                        <Ejemplo titulo="Ejemplo — Alegra GET /api/v1/categories (cuentas contables)">
{`root_path:            (vacio — array en raiz)
is_tree:              ✅
children_field:       children
leaf_filter.field:    use
leaf_filter.equals:   movement      ← solo cuentas operativas
pagination:           (sin paginacion)`}
                        </Ejemplo>

                        <Ejemplo titulo="Ejemplo — Siigo GET /v1/account-groups (plano paginado)">
{`root_path:            results
is_tree:              ❌
leaf_filter:          (no aplica)
pagination:
  items_path:         results
  next_path:          pagination.next
  style:              url`}
                        </Ejemplo>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </div>
    );
}

function TabMappings() {
    return (
        <div>
            <Alert variant="light" className="small border">
                Un <strong>Mapeo</strong> traduce un campo entre Tinvaz y el proveedor. Se configuran de forma
                visual desde el arbol de campos del modelo Tinvaz.
            </Alert>

            <Accordion defaultActiveKey="0" alwaysOpen>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>1️⃣ Direccion del mapeo — <Badge bg="primary">outbound</Badge> vs <Badge bg="info">inbound</Badge></Accordion.Header>
                    <Accordion.Body className="small">
                        <p>Cambia entre direcciones con los botones <Codigo>outbound</Codigo> / <Codigo>inbound</Codigo> arriba de la tabla. El arbol de campos y la lista de mapeos se filtran por la direccion seleccionada.</p>

                        <Table size="sm" bordered>
                            <thead className="table-light">
                                <tr><th>Direccion</th><th>Flujo</th><th>Casos de uso</th></tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><Badge bg="primary">outbound</Badge></td>
                                    <td>Tinvaz → proveedor</td>
                                    <td>POST de comprobantes, facturas, cotizaciones, creacion de clientes.</td>
                                </tr>
                                <tr>
                                    <td><Badge bg="info">inbound</Badge></td>
                                    <td>proveedor → Tinvaz</td>
                                    <td>Importar PUC, leer terceros existentes, consultar inventario.</td>
                                </tr>
                            </tbody>
                        </Table>

                        <Alert variant="warning" className="mb-0 small">
                            ⚠ Los mapeos <strong>no son simetricos</strong>. El mismo campo puede tener
                            transforms y defaults distintos por direccion (ej: un <Codigo>lookup_map</Codigo>{' '}
                            inbound es el inverso del outbound).
                        </Alert>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                    <Accordion.Header>2️⃣ Crear un mapeo — paso a paso</Accordion.Header>
                    <Accordion.Body className="small">
                        <ol>
                            <li>Selecciona <strong>Proveedor</strong> + <strong>Tipo de documento</strong> + <strong>Direccion</strong>.</li>
                            <li>En el panel izquierdo aparece el <strong>arbol de campos</strong> del modelo Tinvaz. Los campos ya mapeados estan marcados con ✅.</li>
                            <li>Haz clic en un campo <strong>hoja</strong> (sin hijos) para abrir el modal de mapeo.</li>
                            <li>Completa el modal y guarda. El mapeo aparece en la tabla derecha.</li>
                        </ol>
                        <p className="text-muted mb-0">
                            Los grupos con <Codigo>▶</Codigo> se expanden al hacer clic. Los grupos marcados con
                            {' '}<Badge bg="secondary">array</Badge> son listas (ej: <Codigo>items[]</Codigo>).
                        </p>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                    <Accordion.Header>3️⃣ Campos del modal de mapeo</Accordion.Header>
                    <Accordion.Body className="small">
                        <Table size="sm" bordered>
                            <thead className="table-light">
                                <tr><th>Campo</th><th>Descripcion</th></tr>
                            </thead>
                            <tbody>
                                <tr><td><strong>Direccion</strong></td><td>outbound o inbound. Cambia las etiquetas del modal.</td></tr>
                                <tr><td><strong>internal_path</strong></td><td>Campo en Tinvaz (readonly, viene del arbol). En inbound es <em>destino</em>; en outbound es <em>origen</em>.</td></tr>
                                <tr><td><strong>external_path</strong></td><td>Campo en el JSON del proveedor. Usa notacion punto: <Codigo>address.city</Codigo>, <Codigo>items.0.code</Codigo>.</td></tr>
                                <tr><td><strong>Requerido</strong></td><td>Solo outbound. Marca si el proveedor exige el campo. El motor lanza error si falta.</td></tr>
                                <tr><td><strong>Origen del valor</strong></td><td>Ver acordeon siguiente.</td></tr>
                                <tr><td><strong>Transform</strong></td><td>Transformacion aplicada al valor (incluye <Codigo>lookup_table</Codigo>).</td></tr>
                                <tr><td><strong>lookup_map</strong></td><td>Aparece solo si <Codigo>transform=lookup_table</Codigo>. JSON con el diccionario de reemplazo.</td></tr>
                                <tr><td><strong>Notas</strong></td><td>Comentario libre.</td></tr>
                            </tbody>
                        </Table>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="3">
                    <Accordion.Header>4️⃣ Origenes del valor (<Codigo>value_source</Codigo>)</Accordion.Header>
                    <Accordion.Body className="small">
                        <Table size="sm" bordered>
                            <thead className="table-light">
                                <tr><th>Origen</th><th>Descripcion</th><th>Cuando usar</th></tr>
                            </thead>
                            <tbody>
                                <tr><td><Codigo>internal</Codigo></td><td>Toma el valor del campo origen tal cual.</td><td>Caso normal.</td></tr>
                                <tr><td><Codigo>default</Codigo></td><td>Usa el campo origen si existe; si no, usa <Codigo>default_value</Codigo>.</td><td>Cuando el campo es opcional pero el destino lo necesita.</td></tr>
                                <tr><td><Codigo>fixed</Codigo></td><td>Siempre usa <Codigo>default_value</Codigo>, ignora el origen.</td><td>Para constantes (ej: enviar siempre <Codigo>tipo: "PAGO"</Codigo>).</td></tr>
                                <tr><td><Codigo>calculated</Codigo></td><td>Logica custom por codigo.</td><td>Reservado, no implementado todavia.</td></tr>
                            </tbody>
                        </Table>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="4">
                    <Accordion.Header>5️⃣ Transformaciones</Accordion.Header>
                    <Accordion.Body className="small">
                        <Table size="sm" bordered>
                            <thead className="table-light">
                                <tr><th>Transform</th><th>Efecto</th></tr>
                            </thead>
                            <tbody>
                                <tr><td><Codigo>none</Codigo></td><td>Sin transformacion.</td></tr>
                                <tr><td><Codigo>number</Codigo></td><td>Convierte a numero.</td></tr>
                                <tr><td><Codigo>string</Codigo></td><td>Convierte a texto.</td></tr>
                                <tr><td><Codigo>boolean</Codigo></td><td>Convierte a booleano.</td></tr>
                                <tr><td><Codigo>date_iso</Codigo></td><td>Fecha ISO completa: <Codigo>YYYY-MM-DDTHH:mm:ssZ</Codigo>.</td></tr>
                                <tr><td><Codigo>date_short</Codigo></td><td>Fecha corta: <Codigo>YYYY-MM-DD</Codigo>.</td></tr>
                                <tr><td><Codigo>uppercase</Codigo></td><td>Texto a MAYUSCULAS.</td></tr>
                                <tr><td><Codigo>lowercase</Codigo></td><td>Texto a minusculas.</td></tr>
                                <tr><td><Codigo>lookup_table</Codigo></td><td>Reemplaza el valor segun el diccionario <Codigo>lookup_map</Codigo>. Si la clave no existe, devuelve el valor original.</td></tr>
                            </tbody>
                        </Table>

                        <Ejemplo titulo="lookup_table inbound — naturaleza contable de Alegra">
{`Direccion:     inbound
external_path: nature
internal_path: naturaleza
transform:     lookup_table
lookup_map:    { "debit": "Débito", "credit": "Crédito" }

Resultado:  Alegra envia "debit"  →  Tinvaz guarda "Débito"`}
                        </Ejemplo>

                        <Ejemplo titulo="lookup_table outbound — el inverso para envio">
{`Direccion:     outbound
internal_path: naturaleza
external_path: nature
transform:     lookup_table
lookup_map:    { "Débito": "debit", "Crédito": "credit" }

Resultado:  Tinvaz envia "Débito"  →  Alegra recibe "debit"

⚠ Notar que el diccionario es el INVERSO. Por eso outbound e
   inbound son mapeos separados.`}
                        </Ejemplo>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="5">
                    <Accordion.Header>6️⃣ Panel de prueba — <Badge bg="primary">solo outbound</Badge></Accordion.Header>
                    <Accordion.Body className="small">
                        <p>
                            En el panel <strong>▶ Probar Motor de Mapeo</strong> puedes pegar un JSON de muestra
                            del documento Tinvaz y ver el resultado transformado que se enviaria al proveedor.
                            Util para validar todos los mapeos outbound antes de sincronizar de verdad.
                        </p>
                        <p className="mb-0 text-muted">
                            Para validar mapeos <strong>inbound</strong> ejecuta el endpoint real de lectura
                            (ej: importar cuentas desde el formulario de cuentas contables) y verifica los datos resultantes.
                        </p>

                        <Ejemplo titulo="Ejemplo — Prueba outbound de cliente">
{`Entrada (documento Tinvaz):
{
  "identificacion": "900123456",
  "nombre": "empresa ejemplo ltda",
  "correo": "info@empresa.com"
}

Resultado (JSON para Alegra):
{
  "identification": "900123456",
  "name": "EMPRESA EJEMPLO LTDA",
  "email": "info@empresa.com",
  "phonePrimary": "Sin teléfono"        ← default
}`}
                        </Ejemplo>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </div>
    );
}

function TabCasoAlegra() {
    return (
        <div>
            <Alert variant="info" className="small">
                Caso completo: importar el plan de cuentas contables (PUC) desde Alegra hacia Tinvaz.
                Es el escenario tipico de un endpoint <Badge bg="info">inbound</Badge> con respuesta en arbol.
            </Alert>

            <Accordion defaultActiveKey="0" alwaysOpen>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Paso 1 — Crear el Proveedor "Alegra"</Accordion.Header>
                    <Accordion.Body className="small">
                        <Ejemplo titulo="Tab Proveedores → + Agregar Proveedor">
{`Nombre interno:    alegra
Nombre visible:    Alegra
URL base:          https://api.alegra.com
Tipo auth:         basic
  username:        <tu_correo_alegra>
  password:        <tu_token_alegra>
Headers globales:  { "Content-Type": "application/json" }`}
                        </Ejemplo>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                    <Accordion.Header>Paso 2 — Crear el Endpoint de cuentas contables</Accordion.Header>
                    <Accordion.Body className="small">
                        <Ejemplo titulo="Tab Endpoints → + Agregar Endpoint">
{`Proveedor:           Alegra
Tipo de documento:   Cuenta Contable (PUC)
Endpoint:            /api/v1/categories
Headers:             (heredados del proveedor)

Forma de la respuesta:
  root_path:           (vacio)
  is_tree:             ✅
  children_field:      children
  leaf_filter.field:   use
  leaf_filter.equals:  movement`}
                        </Ejemplo>

                        <Alert variant="warning" className="small mb-0">
                            El campo <Codigo>leaf_filter</Codigo> con <Codigo>use=movement</Codigo> es lo que evita
                            traer cuentas padre (acumulativas) y solo trae las hojas operativas. Sin esto, traerias
                            todo el arbol mezclado.
                        </Alert>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                    <Accordion.Header>Paso 3 — Crear los 6 mapeos inbound</Accordion.Header>
                    <Accordion.Body className="small">
                        <p>
                            En el tab <strong>Mapeo de Campos</strong>, selecciona <strong>Alegra</strong>,{' '}
                            <strong>Cuenta Contable</strong>, y cambia el toggle a{' '}
                            <Badge bg="info">inbound</Badge>. Despues haz clic en cada campo del arbol y crea
                            estos 6 mapeos:
                        </p>

                        <Table size="sm" bordered>
                            <thead className="table-light">
                                <tr>
                                    <th>external_path</th>
                                    <th>internal_path</th>
                                    <th>transform</th>
                                    <th>Notas</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr><td><Codigo>id</Codigo></td><td><Codigo>external_id</Codigo></td><td><Codigo>string</Codigo></td><td>ID del proveedor (referencia futura).</td></tr>
                                <tr><td><Codigo>code</Codigo></td><td><Codigo>codigo</Codigo></td><td><Codigo>string</Codigo></td><td>Codigo PUC.</td></tr>
                                <tr><td><Codigo>name</Codigo></td><td><Codigo>nombre</Codigo></td><td><Codigo>string</Codigo></td><td>Nombre de la cuenta.</td></tr>
                                <tr><td><Codigo>description</Codigo></td><td><Codigo>descripcion</Codigo></td><td><Codigo>string</Codigo></td><td>value_source=<Codigo>default</Codigo>, default_value=<Codigo>""</Codigo></td></tr>
                                <tr>
                                    <td><Codigo>nature</Codigo></td>
                                    <td><Codigo>naturaleza</Codigo></td>
                                    <td><Codigo>lookup_table</Codigo></td>
                                    <td>lookup_map: <Codigo>{`{"debit":"Débito","credit":"Crédito"}`}</Codigo></td>
                                </tr>
                                <tr>
                                    <td><Codigo>type</Codigo></td>
                                    <td><Codigo>clase</Codigo></td>
                                    <td><Codigo>lookup_table</Codigo></td>
                                    <td>lookup_map con <Codigo>asset→1-Activo</Codigo>, <Codigo>liability→2-Pasivo</Codigo>, etc.</td>
                                </tr>
                            </tbody>
                        </Table>

                        <p className="text-muted mb-0">
                            Atajo: en el repo hay un script seed (<Codigo>seedAlegraCuentasMapping.js</Codigo>)
                            que crea estos 6 mapeos en una sola corrida.
                        </p>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="3">
                    <Accordion.Header>Paso 4 — Marcar Alegra como proveedor contable activo</Accordion.Header>
                    <Accordion.Body className="small">
                        <p>
                            En <strong>Configuracion contable global</strong> (modelo <Codigo>ConfiguracionContable</Codigo>),
                            asigna el campo <Codigo>providerContable</Codigo> al ID del proveedor Alegra. Esto le
                            dice al sistema que cuando alguien abra el selector de cuentas externas, debe
                            consultar Alegra.
                        </p>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="4">
                    <Accordion.Header>Paso 5 — Verificar el flujo end-to-end</Accordion.Header>
                    <Accordion.Body className="small">
                        <ol>
                            <li>Ve a <Codigo>/ConexionContable/CC</Codigo> → clic en <strong>Crear cuenta</strong>.</li>
                            <li>En el modal aparece el <strong>SelectorCuentaExterna</strong> con un autocomplete.</li>
                            <li>Busca por codigo o nombre (ej: "caja"). Selecciona una cuenta.</li>
                            <li>Los campos del formulario se prerellenan: codigo, nombre, naturaleza, clase, descripcion, external_id.</li>
                            <li>Edita lo que necesites y guarda.</li>
                        </ol>

                        <Alert variant="success" className="mb-0 small">
                            ✅ Si todo esta bien configurado, la primera carga consulta Alegra (puede tardar unos segundos)
                            y las siguientes son instantaneas gracias a la cache de React Query (1 hora de frescura).
                        </Alert>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="5">
                    <Accordion.Header>🔧 Troubleshooting</Accordion.Header>
                    <Accordion.Body className="small">
                        <dl className="mb-0">
                            <dt>El selector dice "No hay proveedor configurado"</dt>
                            <dd>
                                Revisar Paso 4: <Codigo>ConfiguracionContable.providerContable</Codigo> debe
                                apuntar al ID del proveedor Alegra.
                            </dd>
                            <dt>El selector dice "No se pudo consultar el proveedor"</dt>
                            <dd>
                                Credenciales invalidas o URL base mal. Revisar Paso 1. El error completo se ve
                                en el log del backend.
                            </dd>
                            <dt>El selector lista cuentas pero sin codigo o nombre</dt>
                            <dd>
                                Los mapeos inbound no estan creados o estan inactivos. Revisar Paso 3, en
                                particular <Codigo>code→codigo</Codigo> y <Codigo>name→nombre</Codigo>.
                            </dd>
                            <dt>La naturaleza aparece como "debit" en vez de "Débito"</dt>
                            <dd>
                                El <Codigo>lookup_map</Codigo> del mapeo <Codigo>nature</Codigo> no esta
                                configurado o tiene JSON invalido.
                            </dd>
                            <dt>El selector trae demasiadas cuentas (incluye padres)</dt>
                            <dd>
                                El <Codigo>leaf_filter</Codigo> del endpoint no esta configurado. Revisar Paso 2:
                                debe ser <Codigo>use = movement</Codigo>.
                            </dd>
                        </dl>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </div>
    );
}

function HelpModal({ onClose }) {
    const [activeTab, setActiveTab] = useState('inicio');

    return (
        <>
            <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1055 }}>
                <div className="modal-dialog modal-xl modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header bg-primary text-white">
                            <h5 className="modal-title">
                                📖 Manual de Configuracion Contable
                            </h5>
                            <button type="button" className="btn-close btn-close-white" onClick={onClose} />
                        </div>
                        <div className="modal-body" style={{ fontSize: 14 }}>

                            <Tabs
                                activeKey={activeTab}
                                onSelect={(k) => setActiveTab(k)}
                                className="mb-3"
                                fill
                            >
                                <Tab eventKey="inicio" title="🏁 Inicio">
                                    <TabInicio />
                                </Tab>
                                <Tab eventKey="providers" title="🏢 Proveedores">
                                    <TabProveedores />
                                </Tab>
                                <Tab eventKey="endpoints" title="🔌 Endpoints">
                                    <TabEndpoints />
                                </Tab>
                                <Tab eventKey="mappings" title="🔁 Mapeo de Campos">
                                    <TabMappings />
                                </Tab>
                                <Tab eventKey="alegra" title="🎯 Caso: Alegra">
                                    <TabCasoAlegra />
                                </Tab>
                            </Tabs>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>Cerrar</button>
                        </div>
                    </div>
                </div>
            </div>
            <div
                className="modal-backdrop fade show"
                style={{ zIndex: 1054 }}
                onClick={onClose}
            />
        </>
    );
}

export default function ConfiguracionContable() {
    const [activeTab, setActiveTab] = useState('providers');
    const [showManual, setShowManual] = useState(false);

    return (
        <div className="container-fluid py-3">
            <div className="d-flex align-items-center gap-2 mb-3">
                <h4 className="mb-0">Configuración Contable</h4>
                <button
                    className="btn btn-sm btn-outline-secondary"
                    title="Abrir manual de ayuda"
                    onClick={() => setShowManual(true)}
                >
                    ❓ Ayuda
                </button>
            </div>

            <ul className="nav nav-tabs mb-3">
                {TABS.map(tab => (
                    <li className="nav-item" key={tab.key}>
                        <button
                            className={`nav-link${activeTab === tab.key ? ' active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    </li>
                ))}
            </ul>

            {activeTab === 'providers'      && <ProveedoresTab />}
            {activeTab === 'endpoints'      && <EndpointsTab />}
            {activeTab === 'field-mappings' && <FieldMappingsTab />}

            {showManual && <HelpModal onClose={() => setShowManual(false)} />}
        </div>
    );
}
